/* ─────────────────────────────────────────────────────────────────────────────
   sound.js — AGENTSEE's audio floor, synthesised end to end.

   No files. No network. Every bed, every cue, every footstep and the room
   reverb itself are built out of noise buffers, oscillators and biquads at
   runtime. Nothing makes a sound until start() is called from a gesture, and
   every method is safe to call before that — place() and setMode() queue.

   The rule the whole file is tuned against: it must survive ten minutes.
   That means no sustained pure tones, nothing above ~9 kHz in the bed, no
   energy parked in the 2–5 kHz region the ear is cruellest about, and loop
   points that are crossfaded so the beds never tick.
   ────────────────────────────────────────────────────────────────────────── */

const LN60 = 6.907755;                                   // ln(1000) — the −60 dB constant for RT60 envelopes

export function buildSound(opts = {}) {

  /* ---------- configuration ---------- */
  const CFG = {
    volume:       opts.volume       ?? 0.85,
    context:      opts.context      || null,   // hand in main.js's audioCtx to collapse the two audio threads
    panningModel: opts.panningModel || 'HRTF',
    cheapLowEnd:  opts.cheapLowEnd  ?? false,  // lamp/server drop to equalpower — sub-300 Hz gains nothing from HRTF
    maxVoices:    opts.maxVoices    ?? 18,
    maxEmitters:  opts.maxEmitters  ?? 28,
    rt60:         opts.rt60         ?? 1.10,   // see makeIR() for why 1.1 s
    listenerHz:   opts.listenerHz   ?? 30,
    suspendHidden:opts.suspendHidden?? true,
    debug:        opts.debug        ?? false,  // wires ctx.renderCapacity so the audio thread can be measured
  };

  /* ---------- state that exists before the context does ---------- */
  let ctx = CFG.context;
  let built = false, running = false, muted = false, failed = false;
  let volume = CFG.volume;
  let mode = 'plan', modeSecs = 0.9;
  const queued = new Map();                    // place() calls made before start()
  const emitters = new Map();
  let live = 0;                                // concurrent one-shot sources
  const lastCue = new Map();                   // per-cue rate limit, in ctx time

  /* graph handles — all null until build() */
  let master, mute, tilt, hp, limiter;
  let bed, bedDuck, bedSend, spatial, spatialDuck, spatialSend, sfx, verbIn, wet, convolver;
  let WHITE = null, PINK = null;
  const pinkSrc = [], whiteSrc = [];
  const BED = {};                              // name → GainNode
  let modSlow = null;                          // shared slow-noise modulator (pink → 2.5 Hz LP)

  /* listener tracking */
  let cam = null, listenAcc = 0;
  const LP = [0, 1.6, 0], LF = [0, 0, -1], LU = [0, 1, 0];

  /* ducking */
  let duckUntil = 0, ducked = false;

  /* schedulers — all cursors live in ctx.currentTime, never in accumulated dt,
     so window.__fleet.pump() firing LOOP 60× in one tick cannot machine-gun them */
  let nextKeys = 0, nextPrint = 0, nextPaper = 0;

  /* ---------- the mode matrix ---------- *
     One set of bed nodes exists for the whole app; a mode is a gain profile
     over them. keys/print are MEAN SECONDS between distant events (0 = never). */
  const MODES = {
    plan: { rumble:0.16, air:0.00, city:0.34, glass:0.00, fluoro:0.00,
            bed:0.62, spatial:0.00, wet:0.18, keys:0,  print:0,  paper:0  },
    walk: { rumble:0.55, air:0.30, city:0.26, glass:0.26, fluoro:0.20,
            bed:1.00, spatial:1.00, wet:0.34, keys:7.5, print:34, paper:19 },
    meet: { rumble:0.30, air:0.12, city:0.10, glass:0.07, fluoro:0.10,
            bed:0.72, spatial:0.55, wet:0.12, keys:15,  print:70, paper:40 },
  };
  const M = () => MODES[mode] || MODES.plan;

  /* ═══════════════════════════════════════════════════════════════════════
     BUFFERS
     ═══════════════════════════════════════════════════════════════════════ */

  /* A looping noise buffer whose wrap point is inaudible. We generate N+X
     samples and fold the overhang back over the head with a raised cosine, so
     d[n-1] runs continuously into d[0]. Without this every bed ticks once per
     loop — which is exactly the artefact that makes minute nine unbearable. */
  function noiseBuffer(seconds, pink) {
    const sr = ctx.sampleRate;
    const n = Math.floor(sr * seconds);
    const X = Math.min(2048, n >> 3);
    const s = new Float32Array(n + X);
    if (pink) {                                          // Paul Kellett's economy pink filter
      let b0=0,b1=0,b2=0,b3=0,b4=0,b5=0,b6=0;
      for (let i = 0; i < n + X; i++) {
        const w = Math.random() * 2 - 1;
        b0 = 0.99886*b0 + w*0.0555179; b1 = 0.99332*b1 + w*0.0750759;
        b2 = 0.96900*b2 + w*0.1538520; b3 = 0.86650*b3 + w*0.3104856;
        b4 = 0.55000*b4 + w*0.5329522; b5 = -0.7616*b5 - w*0.0168980;
        s[i] = (b0+b1+b2+b3+b4+b5+b6 + w*0.5362) * 0.11;
        b6 = w * 0.115926;
      }
    } else {
      for (let i = 0; i < n + X; i++) s[i] = Math.random() * 2 - 1;
    }
    const buf = ctx.createBuffer(1, n, sr);
    const d = buf.getChannelData(0);
    for (let i = 0; i < n; i++) d[i] = s[i];
    for (let i = 0; i < X; i++) {                         // fold the overhang into the head
      const w = 0.5 - 0.5 * Math.cos(Math.PI * i / X);    // 0 → 1
      d[i] = s[i] * w + s[n + i] * (1 - w);
    }
    return buf;
  }

  /* ---------- the room: a procedurally generated impulse response ----------
     RT60 = 1.10 s at mid frequencies.

     The floor is roughly 52 m × 24 m with glass curtain walls, parquet and
     concrete underfoot and hard partitions — a carpeted open-plan office of
     this volume measures 0.6–0.9 s, and the hard surfaces here push it up.
     1.1 s is long enough that the corridor answers you and short enough that
     speech stays intelligible; past ~1.4 s an office starts to read as a
     church, which is the wrong building.

     Frequency-dependent decay without a filterbank: the IR is the SUM of two
     decaying noise layers — a BRIGHT layer of full-band noise at RT60 0.42 s,
     and a DARK layer of noise through two cascaded one-poles at 700 Hz
     (−12 dB/octave) decaying over the full 1.10 s. Highs die first, exactly as
     air absorption and soft furnishing make them.

     The two layers are each normalised on their own early RMS before they are
     mixed. That matters: a low-pass costs amplitude, so mixing raw layers lets
     the filter's incidental loss set the bright/dark balance instead of the
     design setting it — which is how the frequency-dependent decay silently
     collapses back into a flat one.

     On top: an 11 ms predelay (the walls are not on your face) and six sparse
     discrete early reflections alternating between the channels, which is what
     gives the tail a stereo width raw noise cannot.

     normalize=false and a unity-energy scaling, so a wet gain of 0.34 means
     0.34 and does not drift with the sample rate. */
  function makeIR(rt60) {
    const sr = ctx.sampleRate;
    const pre = Math.floor(sr * 0.011);
    const len = pre + Math.ceil(sr * (rt60 * 1.06));
    const buf = ctx.createBuffer(2, len, sr);
    const A = 1 - Math.exp(-2 * Math.PI * 700 / sr);      // one-pole coefficient for 700 Hz
    const BRIGHT_RT = 0.42, MIX = 0.85;                   // dark energy relative to bright, early on
    const TAPS = [ [0.0069,0.54], [0.0131,0.43], [0.0197,0.35],
                   [0.0288,0.27], [0.0411,0.20], [0.0563,0.15] ];
    const br = new Float32Array(len), dk = new Float32Array(len);
    for (let ch = 0; ch < 2; ch++) {
      const d = buf.getChannelData(ch);
      let p1 = 0, p2 = 0;
      for (let i = pre; i < len; i++) {
        const t = (i - pre) / sr;
        const w = Math.random() * 2 - 1;
        p1 += (w - p1) * A; p2 += (p1 - p2) * A;          // −12 dB/oct from 700 Hz
        br[i] = w  * Math.exp(-LN60 * t / BRIGHT_RT);
        dk[i] = p2 * Math.exp(-LN60 * t / rt60);
      }
      const win = Math.min(len - pre, Math.floor(sr * 0.030));
      const rms = (a) => { let s = 0; for (let i = pre; i < pre + win; i++) s += a[i] * a[i]; return Math.sqrt(s / win) || 1; };
      const gb = 1 / rms(br), gd = MIX / rms(dk);
      for (let i = pre; i < len; i++) d[i] = br[i] * gb + dk[i] * gd;
      TAPS.forEach(([tt, g], k) => {                      // early reflections, alternating sides
        if ((k & 1) !== ch) return;
        const p = pre + Math.floor(tt * sr);
        if (p < len) d[p] += g * (Math.random() < 0.5 ? -1 : 1);
      });
    }
    let e = 0;
    for (let ch = 0; ch < 2; ch++) { const d = buf.getChannelData(ch); for (let i = 0; i < len; i++) e += d[i]*d[i]; }
    const k = 1 / Math.sqrt(e || 1);
    for (let ch = 0; ch < 2; ch++) { const d = buf.getChannelData(ch); for (let i = 0; i < len; i++) d[i] *= k; }
    return buf;
  }

  /* ═══════════════════════════════════════════════════════════════════════
     SMALL HELPERS
     ═══════════════════════════════════════════════════════════════════════ */

  const now = () => ctx.currentTime;
  const rnd = (a, b) => a + Math.random() * (b - a);

  function gain(v) { const g = ctx.createGain(); g.gain.value = v; return g; }
  function filt(type, freq, Q, gainDb) {
    const f = ctx.createBiquadFilter();
    f.type = type; f.frequency.value = freq;
    if (Q !== undefined) f.Q.value = Q;
    if (gainDb !== undefined) f.gain.value = gainDb;
    return f;
  }
  function lfo(freq, depth, phase) {                      // audio-rate LFO: costs no JS at all
    const o = ctx.createOscillator(); o.type = 'sine'; o.frequency.value = freq;
    const g = gain(depth);
    o.connect(g); o.start(now() + (phase || 0));
    return { o, g, out: g };
  }
  /* a looping tap off one of the shared noise sources — decorrelated by which
     source it takes and by that source's start offset */
  function pinkTap(i) { const g = gain(1); pinkSrc[i % pinkSrc.length].connect(g); return g; }
  function whiteTap(i) { const g = gain(1); whiteSrc[i % whiteSrc.length].connect(g); return g; }

  /* ---------- one-shot scaffolding ----------
     A shot owns its nodes and tears the whole chain down when its last source
     ends. Sources are counted against maxVoices so a storm can never stack. */
  function budget(n) { return live + n <= CFG.maxVoices; }

  function mkShot(o = {}) {
    const s = { nodes: [], pending: 0, in: null };
    const head = gain(o.gain ?? 1);
    s.nodes.push(head); s.in = head;
    let tail = head;
    if (o.pos) {
      const p = ctx.createPanner();
      p.panningModel = CFG.panningModel; p.distanceModel = 'inverse';
      p.refDistance = o.ref || 1.4; p.maxDistance = o.max || 24; p.rolloffFactor = o.roll || 1.5;
      pannerAt(p, o.pos[0], o.pos[1], o.pos[2]);
      head.connect(p); s.nodes.push(p); tail = p;
    } else if (o.pan) {
      const sp = ctx.createStereoPanner(); sp.pan.value = o.pan;
      head.connect(sp); s.nodes.push(sp); tail = sp;
    }
    tail.connect(o.dest || sfx);
    const w = o.wet ?? 0.22;
    if (w > 0) { const snd = gain(w); tail.connect(snd); snd.connect(verbIn); s.nodes.push(snd); }
    s.add = (src) => {
      s.pending++; live++;
      src.onended = () => {
        live--;
        try { src.disconnect(); } catch (e) {}
        if (--s.pending === 0) s.nodes.forEach(n => { try { n.disconnect(); } catch (e) {} });
      };
    };
    return s;
  }

  /* percussive envelope: exponential everywhere, because linear decays sound
     synthetic and exponential ones sound like objects */
  function env(g, t, attack, dur, peak) {
    g.gain.setValueAtTime(0.00008, t);
    g.gain.exponentialRampToValueAtTime(Math.max(0.0002, peak), t + attack);
    g.gain.exponentialRampToValueAtTime(0.00008, t + dur);
    g.gain.setValueAtTime(0, t + dur + 0.004);
  }

  function tone(s, type, freq, t, dur, peak, attack = 0.004, dest) {
    const o = ctx.createOscillator(); o.type = type; o.frequency.setValueAtTime(freq, t);
    const g = gain(0); env(g, t, attack, dur, peak);
    o.connect(g); g.connect(dest || s.in); s.nodes.push(g);
    o.start(t); o.stop(t + dur + 0.03); s.add(o);
    return { o, g };
  }

  function burst(s, t, dur, peak, f, attack = 0.0012, dest) {
    const src = ctx.createBufferSource(); src.buffer = WHITE; src.loop = true;
    const g = gain(0); env(g, t, attack, dur, peak);
    let node = src;
    if (f) { src.connect(f); node = f; s.nodes.push(f); }
    node.connect(g); g.connect(dest || s.in); s.nodes.push(g);
    src.start(t, Math.random() * (WHITE.duration - 0.3)); src.stop(t + dur + 0.03); s.add(src);
    return { src, g };
  }

  /* ═══════════════════════════════════════════════════════════════════════
     BUILD
     ═══════════════════════════════════════════════════════════════════════ */

  function build() {
    if (built) return;
    built = true;

    WHITE = noiseBuffer(3.0, false);
    PINK  = noiseBuffer(5.0, true);

    /* master chain, output end first:
       … → master → hp(30) → tilt(-2.5 dB @ 6k) → limiter → destination
       The high-pass keeps the HVAC rumble out of laptop speakers where it only
       turns to distortion; the shelf takes the glare off before the limiter,
       so the limiter never has to work on content the ear hates anyway. */
    limiter = ctx.createDynamicsCompressor();
    limiter.threshold.value = -7; limiter.knee.value = 4; limiter.ratio.value = 12;
    limiter.attack.value = 0.003; limiter.release.value = 0.22;
    limiter.connect(ctx.destination);

    tilt = filt('highshelf', 6000, undefined, -2.5); tilt.connect(limiter);
    hp   = filt('highpass', 30, 0.6);                hp.connect(tilt);
    mute = gain(1);                                  mute.connect(hp);
    master = gain(volume);                           master.connect(mute);

    /* reverb return */
    convolver = ctx.createConvolver();
    convolver.normalize = false;
    convolver.buffer = makeIR(CFG.rt60);
    verbIn = gain(1);
    wet = gain(M().wet);
    verbIn.connect(convolver); convolver.connect(wet); wet.connect(master);

    /* three buses. bed and spatial duck for voice; sfx never does — a cue
       fired under a line of dialogue should still land. */
    bedDuck     = gain(1);  bedDuck.connect(master);
    bed         = gain(M().bed); bed.connect(bedDuck);
    bedSend     = gain(0.30); bedDuck.connect(bedSend); bedSend.connect(verbIn);
    spatialDuck = gain(1);  spatialDuck.connect(master);
    spatial     = gain(M().spatial); spatial.connect(spatialDuck);
    spatialSend = gain(0.34); spatialDuck.connect(spatialSend); spatialSend.connect(verbIn);
    sfx         = gain(1);  sfx.connect(master);

    /* shared noise sources. One source fanning out to everything is cheap but
       correlated — every chain would sum coherently and the bed would collapse
       to a narrow phasey line. Three pink and two white, started at different
       offsets, buys the decorrelation back for four extra nodes. */
    const t0 = now() + 0.02;
    for (let i = 0; i < 3; i++) {
      const s = ctx.createBufferSource(); s.buffer = PINK; s.loop = true;
      s.start(t0, (i * 1.63) % PINK.duration); pinkSrc.push(s);
    }
    for (let i = 0; i < 2; i++) {
      const s = ctx.createBufferSource(); s.buffer = WHITE; s.loop = true;
      s.start(t0, (i * 1.11) % WHITE.duration); whiteSrc.push(s);
    }

    /* Shared slow modulator — the control signal that stops every modulated
       thing in here being a clean sine.

       Not a sub-hertz biquad: at 2.5 Hz against a 48 kHz rate the poles sit so
       close to z=1 that the filter is numerically marginal. Instead the same
       pink buffer is played at 1/50 speed, which shifts its whole spectrum down
       by 50× — 5 s of pink becomes 250 s of slow wander that never audibly
       repeats. Exact, stable, and cheaper than the filter it replaces. */
    {
      const s = ctx.createBufferSource(); s.buffer = PINK; s.loop = true;
      s.playbackRate.value = 0.02;
      const f = filt('lowpass', 40, 0.7);              // an ordinary, safe cutoff
      modSlow = gain(1.4);
      s.connect(f); f.connect(modSlow); s.start(now() + 0.02);
    }

    buildBed();

    /* whatever the world registered before the gesture arrived */
    queued.forEach((q, id) => makeEmitter(id, q.x, q.y, q.z, q.kind));
    queued.clear();

    applyMode(0.001);
    if (CFG.debug) wireDebug();
  }

  /* ---------- the ambient bed ---------- */
  function buildBed() {
    /* 1 · HVAC rumble — pink under 90 Hz, cutoff breathing on a 0.043 Hz LFO
          so the plant never sits perfectly still */
    {
      const f = filt('lowpass', 90, 0.7);
      const f2 = filt('lowpass', 150, 0.5);
      const g = gain(0);
      pinkTap(0).connect(f); f.connect(f2); f2.connect(g); g.connect(bed);
      const l = lfo(0.043, 13); l.out.connect(f.frequency);
      BED.rumble = g;
    }
    /* 2 · duct air — white through a wide 420 Hz band; the sound of moving air,
          not of a fan */
    {
      const f = filt('bandpass', 420, 0.55);
      const f2 = filt('lowpass', 1400, 0.5);
      const g = gain(0);
      whiteTap(0).connect(f); f.connect(f2); f2.connect(g); g.connect(bed);
      const l = lfo(0.071, 46, 0.4); l.out.connect(f.frequency);
      BED.air = g;
    }
    /* 3 · the city, two blocks down — pink twice low-passed, swelling on two
          incommensurate LFOs so the traffic never repeats audibly */
    {
      const f = filt('lowpass', 260, 0.6);
      const f2 = filt('lowpass', 700, 0.5);
      const g = gain(0);
      const swell = gain(1);
      pinkTap(1).connect(f); f.connect(f2); f2.connect(swell); swell.connect(g); g.connect(bed);
      lfo(0.031, 0.22).out.connect(swell.gain);
      lfo(0.017, 0.14, 1.1).out.connect(swell.gain);
      BED.city = g;
    }
    /* 4 · that same city bleeding through glass — a narrow band around 1.1 kHz,
          which is all a sealed curtain wall lets past */
    {
      const f = filt('bandpass', 1100, 0.9);
      const f2 = filt('lowpass', 2600, 0.5);
      const g = gain(0);
      pinkTap(2).connect(f); f.connect(f2); f2.connect(g); g.connect(bed);
      BED.glass = g;
    }
    /* 5 · fluorescent ballast — 100 Hz and 300 Hz (50 Hz mains, AU), amplitude
          modulated by the shared slow noise. Never clean sines: a steady tone
          in the HVAC band beats against the rumble and that beat is the single
          most fatiguing thing an ambient bed can do. Capped near −48 dB. */
    {
      const g = gain(0);
      const shape = filt('lowpass', 900, 0.7);
      const am = gain(1);
      shape.connect(am); am.connect(g); g.connect(bed);
      const d = gain(0.40); modSlow.connect(d); d.connect(am.gain);
      [[100, 0.0040], [300, 0.0016]].forEach(([hz, amp]) => {
        const o = ctx.createOscillator(); o.type = 'sine'; o.frequency.value = hz;
        const og = gain(amp); o.connect(og); og.connect(shape); o.start(now() + 0.02);
      });
      BED.fluoro = g;
    }
  }

  /* ═══════════════════════════════════════════════════════════════════════
     POSITIONAL EMITTERS
     ═══════════════════════════════════════════════════════════════════════ */

  function pannerAt(p, x, y, z) {
    if (p.positionX) { p.positionX.value = x; p.positionY.value = y; p.positionZ.value = z; }
    else p.setPosition(x, y, z);
  }

  const KINDS = {
    /* ref / max / roll shape the inverse distance law; gain is the emitter's
       own headroom before the spatial bus */
    lamp:    { ref: 1.6, max: 12, roll: 1.9, gain: 0.16, low: true },
    screen:  { ref: 1.0, max: 7,  roll: 2.2, gain: 0.13, low: false },
    server:  { ref: 2.0, max: 18, roll: 1.4, gain: 0.50, low: true },
    printer: { ref: 2.0, max: 20, roll: 1.5, gain: 0.55, low: false },
    clock:   { ref: 1.4, max: 11, roll: 1.7, gain: 0.30, low: false },
    street:  { ref: 4.0, max: 42, roll: 1.0, gain: 0.45, low: false },
  };

  function makeEmitter(id, x, y, z, kind) {
    if (emitters.size >= CFG.maxEmitters) return;
    const K = KINDS[kind] || KINDS.lamp;
    const p = ctx.createPanner();
    p.panningModel = (CFG.cheapLowEnd && K.low) ? 'equalpower' : CFG.panningModel;
    p.distanceModel = 'inverse';
    p.refDistance = K.ref; p.maxDistance = K.max; p.rolloffFactor = K.roll;
    pannerAt(p, x, y, z);
    const g = gain(K.gain);
    g.connect(p); p.connect(spatial);

    const e = { id, kind, x, y, z, panner: p, gain: g, base: K.gain, max: K.max,
                nodes: [p, g], culled: false, next: 0 };
    const seed = emitters.size;

    if (kind === 'lamp') {
      /* magnetic ballast: 100 + 200 Hz, noise-modulated, deliberately dull */
      const shape = filt('lowpass', 620, 0.8);
      const am = gain(1); shape.connect(am); am.connect(g);
      const d = gain(0.30); modSlow.connect(d); d.connect(am.gain);
      [[100, 0.055], [200, 0.020]].forEach(([hz, amp]) => {
        const o = ctx.createOscillator(); o.type = 'sine'; o.frequency.value = hz;
        o.detune.value = rnd(-6, 6);
        const og = gain(amp); o.connect(og); og.connect(shape); o.start(now() + 0.02);
        e.nodes.push(o, og);
      });
      e.nodes.push(shape, am, d);

    } else if (kind === 'screen') {
      /* a panel is almost silent and must stay that way: a 120 Hz supply hum
         under a whisper of air. No coil whine — 8 kHz for ten minutes is torture. */
      const o = ctx.createOscillator(); o.type = 'sine'; o.frequency.value = 120;
      const og = gain(0.05); o.connect(og); og.connect(g); o.start(now() + 0.02);
      const f = filt('bandpass', 2400, 1.2); const fg = gain(0.010);
      whiteTap(seed).connect(f); f.connect(fg); fg.connect(g);
      e.nodes.push(o, og, f, fg);

    } else if (kind === 'server') {
      /* fans: broadband under 900 Hz plus one narrow 220 Hz rotor tone, and a
         second detuned rotor so the two beat slowly the way a rack really does */
      const f = filt('lowpass', 900, 0.6); const f2 = filt('highpass', 70, 0.6);
      const fg = gain(0.30);
      pinkTap(seed).connect(f2); f2.connect(f); f.connect(fg); fg.connect(g);
      const w = filt('bandpass', 220, 5.5); const wg = gain(0.09);
      pinkTap(seed + 1).connect(w); w.connect(wg); wg.connect(g);
      e.nodes.push(f, f2, fg, w, wg);

    } else if (kind === 'printer' || kind === 'clock') {
      /* schedulers, not drones — silent until they fire, so they cost nothing */
      e.sched = kind;
      e.next = 0;
      e.tock = false;
    } else if (kind === 'street') {
      const f = filt('bandpass', 300, 0.5); const f2 = filt('lowpass', 900, 0.6);
      const sw = gain(1); const fg = gain(0.34);
      pinkTap(seed + 2).connect(f); f.connect(f2); f2.connect(sw); sw.connect(fg); fg.connect(g);
      lfo(0.023, 0.28).out.connect(sw.gain);
      e.nodes.push(f, f2, sw, fg);
    }

    emitters.set(id, e);
  }

  /* printer page cycle: motor ramps, rollers grab, the sheet lands */
  function printerCycle(e, t) {
    if (!budget(4)) return;
    const s = mkShot({ wet: 0.30, pos: [e.x, e.y, e.z], ref: 2, max: 22, roll: 1.5, gain: 0.5, dest: spatial });
    const o = ctx.createOscillator(); o.type = 'sawtooth';
    o.frequency.setValueAtTime(48, t);
    o.frequency.linearRampToValueAtTime(63, t + 0.25);
    o.frequency.setValueAtTime(63, t + 1.15);
    o.frequency.linearRampToValueAtTime(38, t + 1.45);
    const lp = filt('lowpass', 340, 1.1);
    const g = gain(0);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.075, t + 0.22);
    g.gain.setValueAtTime(0.075, t + 1.1);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 1.5);
    o.connect(lp); lp.connect(g); g.connect(s.in);
    s.nodes.push(lp, g); o.start(t); o.stop(t + 1.55); s.add(o);
    burst(s, t + 0.30, 0.55, 0.030, filt('bandpass', 1500, 0.8), 0.09);   // rollers
    burst(s, t + 1.02, 0.16, 0.045, filt('bandpass', 2600, 1.4), 0.004);  // the sheet
  }

  function clockTick(e, t) {
    if (!budget(2)) return;
    const s = mkShot({ wet: 0.35, pos: [e.x, e.y, e.z], ref: 1.4, max: 11, roll: 1.7, gain: 0.30, dest: spatial });
    burst(s, t, 0.006, 0.28, filt('bandpass', e.tock ? 2900 : 3400, 4.5));
    tone(s, 'triangle', e.tock ? 640 : 720, t, 0.020, 0.05, 0.001);
    e.tock = !e.tock;
  }

  /* ═══════════════════════════════════════════════════════════════════════
     CUES — every one built from noise bursts and enveloped oscillators.
     Restrained by construction: nothing sweeps, nothing arpeggiates, nothing
     resolves upward like a game reward.
     ═══════════════════════════════════════════════════════════════════════ */

  const RATE = {                                   // minimum seconds between retriggers
    'type': 0.022, 'ui-hover': 0.070, 'ui-click': 0.045, 'error': 0.35,
    'door-open': 0.30, 'door-close': 0.30, 'door-denied': 0.40,
    'message-sent': 0.10, 'message-arrive': 0.10, 'approve': 0.60,
    'seat-focus': 0.25, 'scene-start': 0.50, 'scene-end': 0.50,
  };

  const CUES = {

    /* the coral door swings: latch releases, the leaf leaves the frame, air moves */
    'door-open'(t, o) {
      const s = mkShot({ wet: 0.42, gain: o.gain ?? 1, pos: o.pos });
      burst(s, t, 0.014, 0.30, filt('bandpass', 2600, 6));                       // latch
      tone(s, 'sine', 78, t + 0.02, 0.16, 0.20, 0.006);                          // leaf off the frame
      tone(s, 'triangle', 156, t + 0.02, 0.11, 0.055, 0.006);
      const f = filt('bandpass', 900, 0.9);                                      // the swing
      f.frequency.setValueAtTime(900, t + 0.05);
      f.frequency.exponentialRampToValueAtTime(380, t + 0.45);
      burst(s, t + 0.05, 0.42, 0.055, f, 0.12);
    },

    /* and shuts: air first, then the frame, then the latch — the order is the
       whole difference between a door and a sound effect */
    'door-close'(t, o) {
      const s = mkShot({ wet: 0.40, gain: o.gain ?? 1, pos: o.pos });
      const f = filt('bandpass', 420, 0.9);
      f.frequency.setValueAtTime(420, t);
      f.frequency.exponentialRampToValueAtTime(820, t + 0.24);
      burst(s, t, 0.26, 0.050, f, 0.10);
      burst(s, t + 0.25, 0.09, 0.30, filt('lowpass', 260, 1.1), 0.002);          // into the frame
      tone(s, 'sine', 62, t + 0.25, 0.20, 0.28, 0.003);                          // body
      burst(s, t + 0.30, 0.008, 0.22, filt('bandpass', 3100, 7));                // latch snap
    },

    /* refused. Dull is the point: an 8 ms attack and no highs at all, so it
       reads as mass rather than as impact — then a low mains-frequency buzz
       under it, amplitude-chopped at 23 Hz. No descending pitch, no buzzer. */
    'door-denied'(t, o) {
      const s = mkShot({ wet: 0.30, gain: o.gain ?? 1, pos: o.pos });
      const dull = filt('lowpass', 220, 0.8);
      dull.connect(s.in); s.nodes.push(dull);
      tone(s, 'sine', 52, t, 0.26, 0.34, 0.008, dull);
      tone(s, 'sine', 78, t, 0.22, 0.16, 0.010, dull);
      const o2 = ctx.createOscillator(); o2.type = 'sawtooth'; o2.frequency.value = 82;
      const lp = filt('lowpass', 300, 1.2);
      const chop = gain(0);
      env(chop, t + 0.04, 0.05, 0.66, 0.075);
      const am = ctx.createOscillator(); am.type = 'square'; am.frequency.value = 23;
      const amg = gain(0.5);
      am.connect(amg); amg.connect(chop.gain);
      o2.connect(lp); lp.connect(chop); chop.connect(s.in);
      s.nodes.push(lp, chop, amg);
      o2.start(t); o2.stop(t + 0.78); s.add(o2);
      am.start(t); am.stop(t + 0.78); s.add(am);
      burst(s, t + 0.01, 0.05, 0.035, filt('bandpass', 1400, 3), 0.004);         // the faintest metal
    },

    /* sent: two notes a fourth apart, the second late enough to read as a
       consequence rather than a chord. Quiet, lidded at 5 kHz. */
    'message-sent'(t, o) {
      const s = mkShot({ wet: 0.30, gain: o.gain ?? 1 });
      const lid = filt('lowpass', 5000, 0.6); lid.connect(s.in); s.nodes.push(lid);
      tone(s, 'sine', 587.33, t,         0.19, 0.085, 0.004, lid);   // D5
      tone(s, 'sine', 880.00, t + 0.055, 0.22, 0.070, 0.004, lid);   // A5
      tone(s, 'sine', 1760.0, t + 0.055, 0.10, 0.012, 0.004, lid);   // air
    },

    /* arrived: a fifth landing, low note first by 40 ms, with a felt-hammer
       body under it so something has actually touched the desk */
    'message-arrive'(t, o) {
      const s = mkShot({ wet: 0.34, gain: o.gain ?? 1 });
      const lid = filt('lowpass', 4200, 0.6); lid.connect(s.in); s.nodes.push(lid);
      tone(s, 'sine',     440.00, t,        0.26, 0.080, 0.008, lid);  // A4
      tone(s, 'triangle', 659.25, t + 0.04, 0.24, 0.040, 0.008, lid);  // E5
      tone(s, 'sine',      90.00, t,        0.09, 0.070, 0.003);       // body
    },

    /* Ben's YES. Warm and final: a low D triad, staggered entries, a long
       exponential tail and a deliberately generous wet so it blooms into the
       floor and settles. Nothing is allowed to follow it. */
    'approve'(t, o) {
      const s = mkShot({ wet: 0.46, gain: o.gain ?? 1 });
      const warm = filt('lowpass', 2200, 1.1); warm.connect(s.in); s.nodes.push(warm);
      tone(s, 'sine', 146.83, t,         1.15, 0.115, 0.010, warm);   // D3
      tone(s, 'sine', 220.00, t + 0.025, 1.05, 0.075, 0.010, warm);   // A3
      tone(s, 'sine', 293.66, t + 0.050, 0.95, 0.055, 0.012, warm);   // D4
      tone(s, 'sine',  73.42, t,         0.22, 0.130, 0.006);         // felt thud, D2
    },

    /* one key. Randomised in pitch and level every call, because a run of
       identical clicks is the tell that it is a sample. */
    'type'(t, o) {
      const s = mkShot({ wet: 0.14, gain: (o.gain ?? 1) * rnd(0.72, 1.12), pan: rnd(-0.10, 0.10) });
      burst(s, t, 0.006, 0.16, filt('bandpass', 2400 * rnd(0.88, 1.12), 3.5));
      tone(s, 'triangle', 190 * rnd(0.9, 1.1), t, 0.022, 0.045, 0.001);
    },

    /* barely there — it should be felt, not heard. Without it the UI is dead;
       with anything louder it is a toy. */
    'ui-hover'(t, o) {
      const s = mkShot({ wet: 0.06, gain: o.gain ?? 1 });
      tone(s, 'sine', 1174.66, t, 0.045, 0.014, 0.003);
    },

    /* the interface, not the room: nearly dry so it belongs to the glass */
    'ui-click'(t, o) {
      const s = mkShot({ wet: 0.08, gain: o.gain ?? 1 });
      burst(s, t, 0.008, 0.11, filt('bandpass', 1800, 2));
      tone(s, 'sine', 440, t, 0.055, 0.035, 0.002);
    },

    /* the camera lands on a person: a breath of room opening plus one held
       note. High wet — this is the cue that says the space got closer. */
    'seat-focus'(t, o) {
      const s = mkShot({ wet: 0.50, gain: o.gain ?? 1 });
      burst(s, t, 0.46, 0.030, filt('bandpass', 600, 0.8), 0.17);
      tone(s, 'sine', 329.63, t, 0.72, 0.048, 0.045);
      tone(s, 'sine', 493.88, t + 0.06, 0.52, 0.018, 0.060);
    },

    /* the floor starts working: a low detuned swell that opens a low-pass from
       120 to 420 Hz and closes again. No fanfare — the building leans forward. */
    'scene-start'(t, o) {
      const s = mkShot({ wet: 0.38, gain: o.gain ?? 1 });
      const lp = filt('lowpass', 120, 1.4);
      lp.frequency.setValueAtTime(120, t);
      lp.frequency.exponentialRampToValueAtTime(420, t + 0.90);
      lp.frequency.exponentialRampToValueAtTime(160, t + 1.40);
      const g = gain(0);
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.085, t + 0.55);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 1.40);
      lp.connect(g); g.connect(s.in); s.nodes.push(lp, g);
      [55, 55.4, 110].forEach((hz, i) => {
        const os = ctx.createOscillator(); os.type = i === 2 ? 'triangle' : 'sawtooth';
        os.frequency.value = hz;
        const og = gain(i === 2 ? 0.35 : 1); os.connect(og); og.connect(lp);
        s.nodes.push(og); os.start(t); os.stop(t + 1.45); s.add(os);
      });
    },

    /* and lets go: the same voice closing, with one landing note under it */
    'scene-end'(t, o) {
      const s = mkShot({ wet: 0.42, gain: o.gain ?? 1 });
      const lp = filt('lowpass', 420, 1.2);
      lp.frequency.setValueAtTime(420, t);
      lp.frequency.exponentialRampToValueAtTime(90, t + 0.70);
      const g = gain(0);
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.060, t + 0.10);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.80);
      lp.connect(g); g.connect(s.in); s.nodes.push(lp, g);
      [55, 82.4].forEach(hz => {
        const os = ctx.createOscillator(); os.type = 'sawtooth'; os.frequency.value = hz;
        os.connect(lp); os.start(t); os.stop(t + 0.85); s.add(os);
      });
      tone(s, 'sine', 110, t + 0.06, 0.55, 0.070, 0.012);
    },

    /* wrong. The discomfort is the beating between a minor second, not a
       descending wail — it stops you without embarrassing you. */
    'error'(t, o) {
      const s = mkShot({ wet: 0.24, gain: o.gain ?? 1 });
      const lid = filt('lowpass', 1800, 0.8); lid.connect(s.in); s.nodes.push(lid);
      tone(s, 'sine', 293.66, t, 0.30, 0.075, 0.006, lid);   // D4
      tone(s, 'sine', 311.13, t, 0.30, 0.065, 0.006, lid);   // D#4 — the beat
      burst(s, t, 0.07, 0.045, filt('bandpass', 800, 1.4), 0.003);
    },
  };

  /* ---------- footsteps ---------- */
  const FOOT = {
    /* heel click band / body pitch / burst length / wet — the reverb send is
       held low on purpose: a 1.1 s tail at 2 steps a second turns a corridor
       into a cathedral */
    parquet:  { band: 1500, q: 1.6, body: 120, len: 0.014, lvl: 0.115, wet: 0.20 },
    carpet:   { band: 700,  q: 0.8, body:  95, len: 0.026, lvl: 0.075, wet: 0.10 },
    concrete: { band: 2200, q: 2.2, body: 105, len: 0.010, lvl: 0.130, wet: 0.20 },
  };
  let footSide = 1;

  function doFootstep(surface, speed) {
    const F = FOOT[surface] || FOOT.parquet;
    if (!budget(2)) return;
    const sp = Math.max(0, Math.min(1.6, speed === undefined ? 0.55 : speed));
    const lvl = F.lvl * (0.55 + sp * 0.55);
    footSide = -footSide;
    const t = now() + 0.005;
    const s = mkShot({ wet: F.wet, pan: 0.11 * footSide, gain: 1 });
    burst(s, t, F.len, lvl, filt('bandpass', F.band * rnd(0.88, 1.14), F.q));
    tone(s, 'sine', F.body * rnd(0.9, 1.1), t, 0.055, lvl * 0.55, 0.002);
  }

  /* ═══════════════════════════════════════════════════════════════════════
     MODE / DUCK / LISTENER
     ═══════════════════════════════════════════════════════════════════════ */

  function applyMode(seconds) {
    const m = M();
    const tc = Math.max(0.01, (seconds ?? modeSecs) / 3);   // ~95 % of the way in `seconds`
    const t = now();
    const set = (node, v) => { if (node) node.gain.setTargetAtTime(v, t, tc); };
    set(BED.rumble, m.rumble); set(BED.air, m.air); set(BED.city, m.city);
    set(BED.glass, m.glass);   set(BED.fluoro, m.fluoro);
    set(bed, m.bed); set(spatial, m.spatial); set(wet, m.wet);
  }

  function applyDuck(force) {
    const want = duckUntil > now();
    if (!force && want === ducked) return;
    ducked = want;
    const t = now();
    const v = want ? 0.26 : 1;
    const tc = want ? 0.035 : 0.16;          // fast out of the way, slow back — the way a broadcast duck behaves
    bedDuck.gain.setTargetAtTime(v, t, tc);
    spatialDuck.gain.setTargetAtTime(want ? 0.34 : 1, t, tc);
  }

  function syncListener() {
    if (!cam || !cam.matrixWorld) return;
    const e = cam.matrixWorld.elements;
    const px = e[12], py = e[13], pz = e[14];
    let fx = -e[8], fy = -e[9], fz = -e[10];
    let ux = e[4],  uy = e[5],  uz = e[6];
    let n = Math.hypot(fx, fy, fz) || 1; fx /= n; fy /= n; fz /= n;
    n = Math.hypot(ux, uy, uz) || 1;     ux /= n; uy /= n; uz /= n;
    LP[0] = px; LP[1] = py; LP[2] = pz;
    LF[0] = fx; LF[1] = fy; LF[2] = fz;
    LU[0] = ux; LU[1] = uy; LU[2] = uz;
    const L = ctx.listener, t = now(), h = 1 / CFG.listenerHz;
    if (L.positionX) {
      /* linear ramps, not setTargetAtTime: the panner tracks exactly and the
         spec removed doppler, so there is no pitch artefact to smear away */
      L.positionX.linearRampToValueAtTime(px, t + h);
      L.positionY.linearRampToValueAtTime(py, t + h);
      L.positionZ.linearRampToValueAtTime(pz, t + h);
      L.forwardX.linearRampToValueAtTime(fx, t + h);
      L.forwardY.linearRampToValueAtTime(fy, t + h);
      L.forwardZ.linearRampToValueAtTime(fz, t + h);
      L.upX.linearRampToValueAtTime(ux, t + h);
      L.upY.linearRampToValueAtTime(uy, t + h);
      L.upZ.linearRampToValueAtTime(uz, t + h);
    } else {
      L.setPosition(px, py, pz);
      L.setOrientation(fx, fy, fz, ux, uy, uz);
    }
  }

  /* ---------- distant life, scheduled off ctx.currentTime ----------
     Cursors are absolute audio-clock times, never dt accumulators, so
     window.__fleet.pump() calling LOOP sixty times inside one render quantum
     fires each of these at most once. */
  /* Anything two rooms away is a low-pass and a reverb send, nothing more.
     Built once, on the bed bus, so distant life fades with the mode and never
     competes with a cue. */
  let FAR = null;
  function farBus() {
    if (FAR) return FAR;
    FAR = filt('lowpass', 1500, 0.7);
    const g = gain(0.55);
    FAR.connect(g); g.connect(bed);
    const snd = gain(0.55); g.connect(snd); snd.connect(verbIn);
    return FAR;
  }

  function distantKeys(t) {
    const n = 4 + Math.floor(Math.random() * 8);
    if (!budget(n)) return;
    const s = mkShot({ wet: 0, pan: rnd(-0.55, 0.55), gain: 0.30, dest: farBus() });
    let k = t;
    for (let i = 0; i < n; i++) {
      burst(s, k, 0.006, 0.13 * rnd(0.6, 1.1), filt('bandpass', 2300 * rnd(0.88, 1.12), 3.2));
      k += rnd(0.075, 0.22);
    }
  }

  function distantPaper(t) {
    if (!budget(2)) return;
    const s = mkShot({ wet: 0, pan: rnd(-0.5, 0.5), gain: 0.30, dest: farBus() });
    burst(s, t, 0.24, 0.055, filt('bandpass', 3200, 0.8), 0.05);
    burst(s, t + 0.19, 0.12, 0.040, filt('bandpass', 2100, 1.0), 0.02);
  }

  /* ═══════════════════════════════════════════════════════════════════════
     PUBLIC SURFACE
     ═══════════════════════════════════════════════════════════════════════ */

  function start() {
    if (failed) return Promise.resolve(false);
    try {
      if (!ctx) {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) { failed = true; return Promise.resolve(false); }
        ctx = new AC({ latencyHint: 'interactive' });
      }
      build();
      running = true;
      const r = ctx.resume ? ctx.resume() : Promise.resolve();
      return Promise.resolve(r).then(() => { syncListener(); return true; }).catch(() => false);
    } catch (e) { failed = true; return Promise.resolve(false); }
  }

  function setMode(m, seconds) {
    if (!MODES[m]) return;
    mode = m; modeSecs = seconds ?? 0.9;
    if (running) applyMode(modeSecs);
  }

  function setListener(camera) {
    cam = camera || cam;
    if (running && cam) syncListener();
  }

  function update(dt, camera) {
    if (!running || ctx.state !== 'running') return;
    if (camera) cam = camera;
    const d = Math.min(dt || 0, 0.05);                  // pump() safety: never trust a caller's dt

    listenAcc += d;
    if (listenAcc >= 1 / CFG.listenerHz) { listenAcc = 0; syncListener(); }

    applyDuck(false);

    const t = now(), m = M();

    /* distant life */
    if (m.keys > 0) {
      if (!nextKeys) nextKeys = t + rnd(1.5, m.keys);
      else if (t >= nextKeys) { distantKeys(t + 0.02); nextKeys = t + m.keys * rnd(0.45, 1.8); }
    } else nextKeys = 0;
    if (m.print > 0) {
      if (!nextPrint) nextPrint = t + rnd(8, m.print);
      else if (t >= nextPrint) { const p = anyPrinter(); if (p) printerCycle(p, t + 0.02); nextPrint = t + m.print * rnd(0.5, 1.7); }
    } else nextPrint = 0;
    if (m.paper > 0) {
      if (!nextPaper) nextPaper = t + rnd(5, m.paper);
      else if (t >= nextPaper) { distantPaper(t + 0.02); nextPaper = t + m.paper * rnd(0.5, 1.8); }
    } else nextPaper = 0;

    /* emitters: distance cull (one setTargetAtTime on the state change only,
       never per frame) plus the two scheduler kinds */
    emitters.forEach(e => {
      const dx = e.x - LP[0], dz = e.z - LP[2];
      const far = (dx * dx + dz * dz) > (e.max * 1.35) * (e.max * 1.35);
      if (far !== e.culled) {
        e.culled = far;
        e.gain.gain.setTargetAtTime(far ? 0 : e.base, t, 0.25);
      }
      if (e.sched && !far && m.spatial > 0.01) {
        if (e.sched === 'clock') {
          if (!e.next) e.next = t + 1;
          else if (t >= e.next) { clockTick(e, t + 0.01); e.next = t + 1; }
        } else if (e.sched === 'printer') {
          if (!e.next) e.next = t + rnd(15, 55);
          else if (t >= e.next) { printerCycle(e, t + 0.02); e.next = t + rnd(22, 78); }
        }
      }
    });
  }
  function anyPrinter() {
    for (const e of emitters.values()) if (e.kind === 'printer' && !e.culled) return e;
    return null;
  }

  function place(id, x, y, z, kind) {
    if (!running) { queued.set(id, { x, y, z, kind }); return; }
    if (emitters.has(id)) remove(id);
    makeEmitter(id, x, y, z, kind);
  }

  function remove(id) {
    queued.delete(id);
    const e = emitters.get(id);
    if (!e) return;
    emitters.delete(id);
    e.nodes.forEach(n => { try { n.disconnect(); } catch (err) {} try { if (n.stop) n.stop(); } catch (err) {} });
  }

  function cue(name, o) {
    if (!running || ctx.state !== 'running') return;
    const fn = CUES[name];
    if (!fn) return;
    const t = now();
    const min = RATE[name] ?? 0.05;                      // a mousemove storm cannot machine-gun this
    const prev = lastCue.has(name) ? lastCue.get(name) : -1e9;   // …but the first call of a name always passes
    if (prev + min > t) return;
    lastCue.set(name, t);
    if (!budget(4)) return;
    try { fn(t + 0.004, o || {}); } catch (e) {}
  }

  function footstep(surface, speed) {
    if (!running || ctx.state !== 'running') return;
    try { doFootstep(surface, speed); } catch (e) {}
  }

  function duck(seconds) {
    if (!running) return;
    duckUntil = Math.max(duckUntil, now() + Math.max(0.15, seconds || 1.2));
    applyDuck(true);
  }

  function setVolume(v) {
    volume = Math.max(0, Math.min(1.5, v));
    if (running) master.gain.setTargetAtTime(volume, now(), 0.04);
  }

  function setMute(on) {
    muted = !!on;
    if (running) mute.gain.setTargetAtTime(muted ? 0 : 1, now(), 0.05);
  }

  /* ---------- autoplay policy + tab visibility ----------
     Suspending on hide is not cosmetic: it takes the audio render thread to
     zero while three.js has already parked setAnimationLoop. */
  let wasRunning = false;
  document.addEventListener('visibilitychange', () => {
    if (!running || !ctx) return;
    if (document.hidden) {
      wasRunning = ctx.state === 'running';
      if (CFG.suspendHidden && wasRunning) ctx.suspend().catch(() => {});
    } else if (wasRunning) {
      ctx.resume().then(() => { applyMode(0.35); applyDuck(true); }).catch(() => {});
    }
  });

  /* ---------- optional: measure the audio thread for real ---------- */
  let load = 0;
  function wireDebug() {
    try {
      if (ctx.renderCapacity && ctx.renderCapacity.addEventListener) {
        ctx.renderCapacity.addEventListener('update', ev => { load = ev.averageLoad; });
        ctx.renderCapacity.start({ updateInterval: 1 });
      }
    } catch (e) {}
  }

  const api = {
    start, setMode, setListener, update, place, remove, cue, footstep, duck,
    setVolume, mute: setMute,
  };
  Object.defineProperty(api, 'ready',  { get: () => running && !!ctx && ctx.state === 'running' });
  Object.defineProperty(api, 'ctx',    { get: () => ctx });
  Object.defineProperty(api, 'mode',   { get: () => mode });
  Object.defineProperty(api, 'voices', { get: () => live });
  Object.defineProperty(api, 'load',   { get: () => load });   // ctx.renderCapacity average, with opts.debug
  return api;
}
